import React, { FC, Fragment, useContext, useEffect, useMemo } from 'react';

import {
  Button,
  InfoIcon,
  Input,
  MultiSelect,
  Paragraph,
  Select,
} from '@smartshell/ui-kit';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { ENDS, ETaxVariant } from 'cashbox';
import { NDSSelect } from 'components';
import { serviceNameOptions } from 'consts';
import { LocalizationContext } from 'localization';
import { openConfirmDialog } from 'services/Confirmation';
import notificationService from 'services/notificationService';
import {
  ESettingsAliases,
  ISetSettingsRequest,
  SettingsContext,
} from 'settings';

import s from './Cashbox.module.scss';
import { DEFAULT_TAXES_VALUE, ENTITIES_ALIASES } from './constants';
import { ICashboxProps, IFormCashboxValues } from './interfaces';
import { useKKMSOptions } from './useKKMSOptions';
import {
  getEntityOptions,
  getTaxVariantOptions,
  renderKKMOption,
} from './utils';

export const Cashbox: FC<ICashboxProps> = ({ onClose }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        isCashboxEnabled,
        selectedCashboxINN,
        taxes,
        cashierName,
        depositRefillTitle,
      },
    },
    actions: { setSetting, setSettings },
  } = useContext(SettingsContext);

  const { KKMsOptions, isKKMsLoading, loadKKMs } = useKKMSOptions();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormCashboxValues>({
    mode: 'onChange',
    defaultValues: {
      inn: null,
      cashier: '',
      deposit_refill: null,
      taxes: [{ ...DEFAULT_TAXES_VALUE }],
    },
  });
  const { fields, append, remove } = useFieldArray<IFormCashboxValues, 'taxes'>(
    {
      control,
      name: 'taxes',
    }
  );

  useEffect(() => {
    reset({
      inn: selectedCashboxINN,
      cashier: cashierName,
      deposit_refill: depositRefillTitle,
      taxes: taxes.length ? taxes : [{ ...DEFAULT_TAXES_VALUE }],
    });
  }, [selectedCashboxINN, isCashboxEnabled, taxes]);

  useEffect(() => {
    if (!isCashboxEnabled) return;

    loadKKMs();
  }, [isCashboxEnabled]);

  const ENTITY_OPTIONS = useMemo(() => getEntityOptions(t), [t]);
  const TAX_VARIANT_OPTIONS = useMemo(() => getTaxVariantOptions(t), [t]);

  const selectedEntities = watch('taxes').flatMap((tax) => tax.entities ?? []);

  return (
    <form
      className={s.container}
      onSubmit={handleSubmit((values) => {
        if (values.taxes.flatMap((tax) => tax.entities).length < 4) {
          return openConfirmDialog(t, 'notify', {
            title: 'Настройте все категории',
            message:
              'Укажите систему налогообложения и тип налога для каждой категории',
          });
        }

        const list: ISetSettingsRequest['input']['list'] = [
          {
            alias: ESettingsAliases.CASHBOX_SELECTED_INN,
            value: values.inn as string,
          },
          {
            alias: ESettingsAliases.CASHIER_NAME,
            value: values.cashier,
          },
          {
            alias: ESettingsAliases.DEPOSIT_REFILL_TITLE,
            value: values.deposit_refill as string,
          },
        ];

        values.taxes.forEach((tax) => {
          tax.entities?.forEach((entity) => {
            list.push({
              alias: ENTITIES_ALIASES[entity].nds,
              value: tax.nds as ENDS,
            });
            list.push({
              alias: ENTITIES_ALIASES[entity].system,
              value: tax.taxVariant as ETaxVariant,
            });
          });
        });

        if (!isCashboxEnabled) {
          list.push({
            alias: ESettingsAliases.CASHBOX,
            value: '1',
          });
        }

        return setSettings(list)
          .then(() => {
            notificationService.info({
              t,
              type: 'success',
              title: t('setting.updated.all'),
              message: t('setting.updated.cashBox'),
            });

            onClose();
          })
          .catch(() =>
            notificationService.info({
              t,
              type: 'error',
              title: t('errors.error'),
              message: 'Не удалось сохранить настройки кассы',
            })
          );
      })}
    >
      <div className={s.cashbox}>
        <Controller
          control={control}
          render={({
            field: { onChange, ...field },
            fieldState: { error },
          }) => (
            <Select
              {...field}
              options={KKMsOptions}
              onChange={(value) => {
                if (!value) return;

                const newCashbox = KKMsOptions.find(
                  (option) => option.value === value
                )?.original;

                if (!newCashbox?.FN_IsFiscal || !newCashbox.TaxVariant) {
                  return openConfirmDialog(t, 'notify', {
                    title: 'Проверьте настройки кассы',
                    message: (
                      <span>
                        Укажите систему налогообложения в настройках ККМ сервера
                        и переведите кассовый аппарат в фискальный режим.
                      </span>
                    ),
                  });
                }

                if (
                  value &&
                  selectedCashboxINN &&
                  value !== selectedCashboxINN
                ) {
                  return openConfirmDialog(t, 'remove', {
                    title: 'Изменение ККТ',
                    message: 'Вы уверены, что хотите изменить текущий ККТ?',
                    acceptTitle: t('notifyService.confirm.accept'),
                    declineTitle: t('notifyService.confirm.decline'),
                  }).then(({ status }) => {
                    if (status === 'ACCEPTED') {
                      onChange(value);
                    }
                  });
                }

                onChange(value);
              }}
              renderOption={renderKKMOption}
              disabled={isKKMsLoading}
              emptyOptionsPlaceholder="Нет доступных касс для выбора"
              placeholder="Выберите кассовый аппарат"
              error={error?.message}
              disableClear
            />
          )}
          name="inn"
          rules={{ required: t('errors.required') }}
        />
        <Button
          variant="secondary"
          className={s.reload}
          onClick={loadKKMs}
          disabled={isKKMsLoading}
          iconName={'UpdateIcon'}
        >
          Обновить список
        </Button>
      </div>
      <div className={s.cashier_block}>
        <div className={s.cashier}>
          <Controller
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="ФИО или должность в чеке"
                error={error?.message}
              />
            )}
            name="cashier"
            rules={{
              maxLength: {
                value: 128,
                message: t('errors.maxLength', 128),
              },
            }}
          />
          <div className={s.cashier__info}>
            <InfoIcon size="s" color="warning" />
            <Paragraph>
              Если поле не заполнено, то в чеке отобразится оператор смены
            </Paragraph>
          </div>
        </div>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              title="Название пополнения депозита в чеке"
              error={error?.message}
              options={[
                ...serviceNameOptions,
                {
                  value: 'Пополнение депозита',
                  title: 'Пополнение депозита',
                },
              ]}
            />
          )}
          control={control}
          name="deposit_refill"
          rules={{
            required: t('errors.required'),
          }}
        />
      </div>

      {fields.map((item, index) => (
        <Fragment key={item.id}>
          <div className={s.group}>
            <Controller
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiSelect
                  {...field}
                  title="Категория продаж"
                  variant="secondary"
                  options={ENTITY_OPTIONS}
                  error={error?.message}
                  errorWrapperClassName={s.entities}
                  hiddenOptions={selectedEntities.filter(
                    (entity) => !field.value?.includes(entity)
                  )}
                />
              )}
              defaultValue={item.entities}
              name={`taxes.${index}.entities`}
              rules={{ required: t('errors.required') }}
            />
            <div className={s.taxes}>
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    title="Система налогообложения"
                    options={TAX_VARIANT_OPTIONS}
                    error={error?.message}
                    disableClear
                  />
                )}
                defaultValue={item.taxVariant}
                rules={{
                  required: t('errors.required'),
                }}
                name={`taxes.${index}.taxVariant`}
              />
              <Controller
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NDSSelect
                    notBordered={true}
                    {...field}
                    error={error?.message}
                  />
                )}
                defaultValue={item.nds}
                name={`taxes.${index}.nds`}
                rules={{
                  required: t('errors.required'),
                }}
              />
            </div>
            {fields.length > 1 && (
              <Button
                variant="ghost"
                onClick={() => remove(index)}
                className={s.remove}
                iconName="CloseIcon"
              />
            )}
            {fields.length === 1 && (
              <Button
                variant="secondary"
                onClick={() => append({ ...DEFAULT_TAXES_VALUE })}
                className={s.add}
                iconName="CirclePlusIcon"
              >
                {t('add')}
              </Button>
            )}
          </div>
        </Fragment>
      ))}
      <div className={s.buttons_block}>
        {(isDirty || (isCashboxEnabled && !isDirty)) && (
          <Button fullWidth type="submit" disabled={!isDirty || isSubmitting}>
            {t('save')}
          </Button>
        )}
        {!isDirty && !isCashboxEnabled && (
          <Button
            fullWidth
            onClick={() => {
              setSetting({
                alias: ESettingsAliases.CASHBOX,
                value: '1',
              });
              onClose();
            }}
          >
            {t('hook')}
          </Button>
        )}
        <Button
          variant="secondary"
          fullWidth
          disabled={!isCashboxEnabled}
          onClick={() => {
            setSetting({
              alias: ESettingsAliases.CASHBOX,
              value: '0',
            });

            onClose();
          }}
        >
          Отключить интеграцию
        </Button>
      </div>
    </form>
  );
};
